import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui';
import { designTokens } from '../utils/designTokens';

const About = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>About - Boop</title>
        <meta name="description" content="Learn about Boop's mission to democratize AI knowledge through community-driven prompt sharing and fair creator monetization." />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 text-white">
        <div className={`max-w-7xl mx-auto px-${designTokens.spacing.pagePadding} py-16 text-center`}>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Building the world's best library of AI prompts—together.
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
            A community-first platform where creators share, improve, and discover battle-tested AI prompts.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className={`max-w-4xl mx-auto px-${designTokens.spacing.pagePadding} py-16`}>

        {/* Our Story */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Story</h2>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Boop started with a simple observation: great AI prompts are scattered across Discord servers,
              Twitter threads, and Reddit posts. Brilliant creators share their work, but it disappears into
              the void. Meanwhile, newcomers struggle to write effective prompts, wasting time and tokens on
              subpar results.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We built Boop to change that.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Boop is a community-driven platform where anyone can share AI prompts, rate what works, and
              discover battle-tested techniques. Our gamification system (levels, badges, weekly awards)
              celebrates quality contributors. Our marketplace lets top creators monetize their expertise
              while keeping the vast majority of content free.
            </p>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">What Makes Boop Different</h2>
          <div className="grid gap-6">
            <Card>
              <div className={`p-${designTokens.spacing.cardPadding}`}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="mr-3 text-2xl">🤝</span>
                  Community First
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Free content is our foundation, not a funnel to paid tiers. Core features remain
                  accessible to everyone, forever.
                </p>
              </div>
            </Card>

            <Card>
              <div className={`p-${designTokens.spacing.cardPadding}`}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="mr-3 text-2xl">⭐</span>
                  Quality Focused
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Multi-dimensional ratings, active moderation, and seller requirements ensure
                  you find prompts that actually work.
                </p>
              </div>
            </Card>

            <Card>
              <div className={`p-${designTokens.spacing.cardPadding}`}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="mr-3 text-2xl">💎</span>
                  Creator Rewarded
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Earn Boop Coins through contribution, monetize with low barriers, and get fair
                  compensation for quality work.
                </p>
              </div>
            </Card>

            <Card>
              <div className={`p-${designTokens.spacing.cardPadding}`}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="mr-3 text-2xl">📚</span>
                  Knowledge Access
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  No paywalls for learning. Premium tools are optional features for power users,
                  not gates to basic content.
                </p>
              </div>
            </Card>

            <Card>
              <div className={`p-${designTokens.spacing.cardPadding}`}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="mr-3 text-2xl">🌱</span>
                  Built to Last
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Micro-transactions fund growth without VC pressure for aggressive monetization.
                  Sustainable and community-aligned.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Our Principles */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Principles</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <span className="text-purple-600 dark:text-purple-400 font-bold text-xl mr-4">1.</span>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">No Gatekeeping</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Anyone can learn from world-class prompts. Knowledge should be accessible.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-purple-600 dark:text-purple-400 font-bold text-xl mr-4">2.</span>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">Reward Quality</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Great work deserves recognition and compensation. We celebrate contributors.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-purple-600 dark:text-purple-400 font-bold text-xl mr-4">3.</span>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">Transparent Economics</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Clear fees, fair payouts, no hidden costs. You always know where your money goes.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-purple-600 dark:text-purple-400 font-bold text-xl mr-4">4.</span>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">Sustainable Growth</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Platform fees fund development and creator rewards, not investor returns.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-purple-600 dark:text-purple-400 font-bold text-xl mr-4">5.</span>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">Community Safety</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Active moderation protects users and maintains content quality standards.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 rounded-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join the Community</h2>
          <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
            Whether you're a prompt engineering wizard, an AI-curious beginner, or somewhere in between—Boop
            is your community. Create your first prompt, rate what you love, earn your first badge, and help
            us build the future of AI knowledge sharing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="px-6 py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-purple-50 transition-colors"
            >
              Get Started Free
            </Link>
            <Link
              to="/dashboard"
              className="px-6 py-3 bg-purple-700 text-white rounded-lg font-bold hover:bg-purple-800 transition-colors"
            >
              Browse Prompts
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
